import { getTenantContext, getTenantSignal, staleTenantError, TENANT_CHANGED_EVENT } from './tenantContext';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);
export const SESSION_EXPIRED_EVENT = 'sentientos:session-expired';

export class SessionExpiredError extends Error {
  constructor() {
    super('Your session has expired. Please sign in again.');
    this.name = 'SessionExpiredError';
    this.status = 401;
  }
}

/**
 * Fetch an authenticated API resource using the browser's HttpOnly session.
 *
 * Browser code must never read or send the bearer credential. Unsafe methods
 * additionally carry Django's CSRF token, while cookies are included for both
 * same-origin and configured cross-origin development requests.
 */
export async function sessionFetch(url, { csrfToken, headers, ...options } = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const requestHeaders = new Headers(headers);
  const tenant = getTenantContext();
  const tenantSignal = getTenantSignal();
  if (tenant) requestHeaders.set('X-Tenant-Context', tenant);

  // Prevent callers from accidentally reviving the legacy JS bearer-token
  // transport, which takes precedence over the valid HttpOnly cookie.
  requestHeaders.delete('Authorization');

  if (!SAFE_METHODS.has(method) && csrfToken) {
    requestHeaders.set('X-CSRFToken', csrfToken);
  }

  const response = await fetch(url, {
    ...options,
    method,
    headers: requestHeaders,
    credentials: 'include',
    signal: options.signal ? AbortSignal.any([options.signal, tenantSignal]) : tenantSignal,
  });

  if (tenantSignal.aborted) throw staleTenantError();
  if (response.status === 409) {
    const data = await response.clone().json().catch(() => ({}));
    if (data.code === 'tenant_context_changed') {
      globalThis.dispatchEvent(new CustomEvent(TENANT_CHANGED_EVENT));
      throw staleTenantError();
    }
  }
  if (response.status === 401) {
    globalThis.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    throw new SessionExpiredError();
  }
  return response;
}
